import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { ShareIcon, XMarkIcon, TrashIcon, MagnifyingGlassIcon, LinkIcon, UserGroupIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import {
  SHARE_EXPIRY_OPTIONS,
  computeExpiresAt,
  formatShareExpiry,
} from '../../utils/shareExpiryOptions';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: ${theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: ${theme.borderRadius.sm};

  &:hover {
    color: ${theme.colors.text.primary};
    background: ${theme.colors.background.secondary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Subtitle = styled.p`
  margin: 0 0 1.25rem 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 0.75rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.1rem;
  height: 1.1rem;
  color: ${theme.colors.text.secondary};
`;

const UserPicker = styled.div`
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 1rem;
  background: ${theme.colors.background.primary};
`;

const UserOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  background: ${(props) => (props.$selected ? `${theme.colors.primary.main}18` : 'transparent')};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) => (props.$selected ? `${theme.colors.primary.main}22` : theme.colors.background.secondary)};
  }
`;

const UserName = styled.span`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`;

const UserEmail = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const AccessRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Select = styled.select`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
`;

const PrimaryButton = styled.button`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary.main};
  color: white;
  font-weight: ${theme.fontWeights.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
`;

const ShareMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const ShareName = styled.div`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`;

const ShareEmail = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LevelSelect = styled(Select)`
  flex: 0 0 auto;
  min-width: 120px;
  padding: 0.4rem 0.6rem;
  font-size: ${theme.fontSizes.sm};
`;

const RevokeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  color: ${theme.colors.error?.main || '#ef4444'};
  cursor: pointer;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const EmptyText = styled.p`
  margin: 0;
  padding: 1rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-style: italic;
  text-align: center;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? theme.colors.primary.main : 'transparent')};
  background: transparent;
  color: ${(props) => (props.$active ? theme.colors.primary.main : theme.colors.text.secondary)};
  font-weight: ${(props) => (props.$active ? theme.fontWeights.semibold : theme.fontWeights.medium)};
  cursor: pointer;
  margin-bottom: -1px;

  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0.75rem;
  min-height: 72px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  &:hover {
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary.main};
  }
`;

const ShareMetaLine = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

function displayName(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  return name || row.email || 'User';
}

const ShareDashboardModal = ({ isOpen, onClose, dashboard }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [permissionLevel, setPermissionLevel] = useState('viewer');
  const [shares, setShares] = useState([]);
  const [externalShares, setExternalShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [externalLoading, setExternalLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [externalSubmitting, setExternalSubmitting] = useState(false);
  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');
  const [externalMessage, setExternalMessage] = useState('');
  const [externalExpiry, setExternalExpiry] = useState('forever');

  const dashboardId = dashboard?.id;

  const loadShares = async () => {
    if (!dashboardId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/dashboards/${dashboardId}/permissions`);
      setShares(res.data?.permissions || []);
    } catch (err) {
      console.error('Error loading dashboard shares:', err);
      toast.error(err.response?.data?.error || 'Failed to load shares');
      setShares([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExternalShares = async () => {
    if (!dashboardId) return;
    try {
      setExternalLoading(true);
      const res = await api.get(`/api/dashboard-sharing/${dashboardId}/shares`);
      setExternalShares(res.data?.shares || []);
    } catch (err) {
      console.error('Error loading external shares:', err);
      toast.error(err.response?.data?.error || 'Failed to load external shares');
      setExternalShares([]);
    } finally {
      setExternalLoading(false);
    }
  };

  const loadUsers = useCallback(async () => {
    if (!dashboardId) return;
    try {
      setUsersLoading(true);
      const res = await api.get('/api/dashboards/active-users', {
        params: {
          search: search.trim(),
          exclude_dashboard_id: dashboardId,
        },
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [dashboardId, search]);

  useEffect(() => {
    if (isOpen && dashboardId) {
      setActiveTab('users');
      setSearch('');
      setSelectedUserId(null);
      setPermissionLevel('viewer');
      setExternalEmail('');
      setExternalName('');
      setExternalMessage('');
      setExternalExpiry('forever');
      loadShares();
      loadExternalShares();
    }
  }, [isOpen, dashboardId]);

  useEffect(() => {
    if (!isOpen || !dashboardId) return undefined;
    const timer = setTimeout(() => {
      loadUsers();
    }, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [isOpen, dashboardId, search, loadUsers]);

  const handleShare = async () => {
    if (!selectedUserId) {
      toast.error('Select a user from the list');
      return;
    }
    const selected = users.find((u) => u.id === selectedUserId);
    try {
      setSubmitting(true);
      const res = await api.post(`/api/dashboards/${dashboardId}/permissions`, {
        user_id: selectedUserId,
        permission_level: permissionLevel,
      });
      const emailed = res?.data?.emailed !== false;
      toast.success(
        emailed
          ? `Dashboard shared with ${displayName(selected)} — they were notified by email`
          : `Dashboard shared with ${displayName(selected)}`
      );
      setSelectedUserId(null);
      await loadShares();
      await loadUsers();
    } catch (err) {
      console.error('Error sharing dashboard:', err);
      toast.error(err.response?.data?.error || 'Failed to share dashboard');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLevelChange = async (userId, level, label) => {
    try {
      await api.post(`/api/dashboards/${dashboardId}/permissions`, {
        user_id: userId,
        permission_level: level,
      });
      toast.success(`Updated access for ${label}`);
      await loadShares();
    } catch (err) {
      console.error('Error updating share level:', err);
      toast.error(err.response?.data?.error || 'Failed to update access');
      await loadShares();
    }
  };

  const performRevoke = async (userId, label) => {
    try {
      await api.delete(`/api/dashboards/${dashboardId}/permissions/${userId}`);
      toast.success(`Removed access for ${label}`);
      await loadShares();
      await loadUsers();
    } catch (err) {
      console.error('Error revoking share:', err);
      toast.error(err.response?.data?.error || 'Failed to remove share');
    }
  };

  const handleRevoke = (userId, label) => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Remove dashboard access</span>
        <span className="text-sm text-gray-600">
          Remove access for <strong>{label}</strong>? They will no longer see this dashboard.
        </span>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              performRevoke(userId, label);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Remove access
          </button>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
  };

  const handleExternalShare = async () => {
    const email = externalEmail.trim();
    if (!email) {
      toast.error('Enter an email address');
      return;
    }
    try {
      setExternalSubmitting(true);
      const res = await api.post(`/api/dashboard-sharing/${dashboardId}/shares`, {
        recipient_email: email,
        recipient_name: externalName.trim() || undefined,
        share_message: externalMessage.trim() || undefined,
        expires_at: computeExpiresAt(externalExpiry),
      });
      toast.success(`Link sent to ${email}`);
      setExternalEmail('');
      setExternalName('');
      setExternalMessage('');
      setExternalExpiry('forever');
      await loadExternalShares();
      if (res.data?.shareUrl) {
        toast((t) => (
          <div className="flex flex-col space-y-2">
            <span className="text-sm">Share link created. Copy it if needed:</span>
            <button
              type="button"
              className="text-left text-sm text-blue-600 underline break-all"
              onClick={() => {
                navigator.clipboard.writeText(res.data.shareUrl);
                toast.success('Link copied');
                toast.dismiss(t.id);
              }}
            >
              {res.data.shareUrl}
            </button>
          </div>
        ), { duration: 8000 });
      }
    } catch (err) {
      console.error('Error sharing dashboard externally:', err);
      toast.error(err.response?.data?.error || 'Failed to send share link');
    } finally {
      setExternalSubmitting(false);
    }
  };

  const copyExternalLink = (shareToken) => {
    const shareUrl = `${window.location.origin}/shared-dashboard/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  const performRevokeExternal = async (shareId, label) => {
    try {
      await api.delete(`/api/dashboard-sharing/${dashboardId}/shares/${shareId}`);
      toast.success(`Revoked link for ${label}`);
      await loadExternalShares();
    } catch (err) {
      console.error('Error revoking external share:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke link');
    }
  };

  const handleRevokeExternal = (shareId, label) => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Revoke share link</span>
        <span className="text-sm text-gray-600">
          Revoke the link for <strong>{label}</strong>? The link will stop working immediately.
        </span>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              performRevokeExternal(shareId, label);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Revoke link
          </button>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
  };

  if (!isOpen || !dashboard) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <ShareIcon className="w-5 h-5" />
            Share dashboard
          </Title>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <Subtitle>
            Share <strong>{dashboard.dashboard_name}</strong> with another BoatWise user or send a view-only link by email.
          </Subtitle>

          <TabRow>
            <TabButton type="button" $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
              <UserGroupIcon className="w-4 h-4" />
              BoatWise users
            </TabButton>
            <TabButton type="button" $active={activeTab === 'external'} onClick={() => setActiveTab('external')}>
              <LinkIcon className="w-4 h-4" />
              External link
            </TabButton>
          </TabRow>

          {activeTab === 'users' ? (
            <>
          <Label htmlFor="share-user-search">Find user</Label>
          <SearchWrap>
            <SearchIcon />
            <SearchInput
              id="share-user-search"
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </SearchWrap>

          <UserPicker>
            {usersLoading ? (
              <EmptyText>Loading users…</EmptyText>
            ) : users.length === 0 ? (
              <EmptyText>
                {search
                  ? 'No matching users found.'
                  : 'No other active users available to share with.'}
              </EmptyText>
            ) : (
              users.map((user) => (
                <UserOption
                  key={user.id}
                  type="button"
                  $selected={selectedUserId === user.id}
                  onClick={() => setSelectedUserId(user.id)}
                >
                  <UserName>{displayName(user)}</UserName>
                  <UserEmail>{user.email}</UserEmail>
                </UserOption>
              ))
            )}
          </UserPicker>

          <AccessRow>
            <Select
              value={permissionLevel}
              onChange={(e) => setPermissionLevel(e.target.value)}
              aria-label="Access level for selected user"
            >
              <option value="viewer">Viewer — view only</option>
              <option value="editor">Editor — view and edit</option>
            </Select>
            <PrimaryButton type="button" onClick={handleShare} disabled={submitting || !selectedUserId}>
              {submitting ? 'Sharing…' : 'Share'}
            </PrimaryButton>
          </AccessRow>

          <Label>People with access</Label>
          {loading ? (
            <EmptyText>Loading…</EmptyText>
          ) : shares.length === 0 ? (
            <EmptyText>Not shared with anyone yet.</EmptyText>
          ) : (
            <ShareList>
              {shares.map((row) => (
                <ShareRow key={row.id || row.user_id}>
                  <ShareMeta>
                    <ShareName>{displayName(row)}</ShareName>
                    <ShareEmail>{row.email}</ShareEmail>
                  </ShareMeta>
                  <LevelSelect
                    value={row.permission_level || 'viewer'}
                    onChange={(e) => handleLevelChange(row.user_id, e.target.value, displayName(row))}
                    aria-label={`Access level for ${displayName(row)}`}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </LevelSelect>
                  <RevokeButton
                    type="button"
                    title="Remove access"
                    onClick={() => handleRevoke(row.user_id, displayName(row))}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </RevokeButton>
                </ShareRow>
              ))}
            </ShareList>
          )}
            </>
          ) : (
            <>
              <Subtitle style={{ marginBottom: '1rem' }}>
                Email a secure link to someone outside BoatWise. They can view the dashboard without creating an account.
              </Subtitle>

              <Label htmlFor="external-email">Email address</Label>
              <TextInput
                id="external-email"
                type="email"
                placeholder="friend@example.com"
                value={externalEmail}
                onChange={(e) => setExternalEmail(e.target.value)}
                autoComplete="off"
              />

              <Label htmlFor="external-name">Name (optional)</Label>
              <TextInput
                id="external-name"
                type="text"
                placeholder="Recipient name"
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                autoComplete="off"
              />

              <Label htmlFor="external-expiry">Link expires</Label>
              <Select
                id="external-expiry"
                value={externalExpiry}
                onChange={(e) => setExternalExpiry(e.target.value)}
                style={{ width: '100%', marginBottom: '0.75rem' }}
              >
                {SHARE_EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>

              <Label htmlFor="external-message">Personal message (optional)</Label>
              <TextArea
                id="external-message"
                placeholder="Add a note to include in the email…"
                value={externalMessage}
                onChange={(e) => setExternalMessage(e.target.value)}
              />

              <PrimaryButton
                type="button"
                onClick={handleExternalShare}
                disabled={externalSubmitting || !externalEmail.trim()}
                style={{ width: '100%', marginBottom: '1.5rem' }}
              >
                {externalSubmitting ? 'Sending…' : 'Send link by email'}
              </PrimaryButton>

              <Label>Active external links</Label>
              {externalLoading ? (
                <EmptyText>Loading…</EmptyText>
              ) : externalShares.filter((s) => s.is_active !== false).length === 0 ? (
                <EmptyText>No external links yet.</EmptyText>
              ) : (
                <ShareList>
                  {externalShares.filter((s) => s.is_active !== false).map((row) => (
                    <ShareRow key={row.id}>
                      <ShareMeta>
                        <ShareName>{row.recipient_name || row.recipient_email}</ShareName>
                        <ShareEmail>{row.recipient_email}</ShareEmail>
                        <ShareMetaLine>
                          {formatShareExpiry(row.expires_at)}
                          {row.access_count > 0 && ` · ${row.access_count} view${row.access_count === 1 ? '' : 's'}`}
                        </ShareMetaLine>
                      </ShareMeta>
                      <SecondaryButton type="button" onClick={() => copyExternalLink(row.share_token)}>
                        <ClipboardDocumentIcon className="w-4 h-4" />
                        Copy
                      </SecondaryButton>
                      <RevokeButton
                        type="button"
                        title="Revoke link"
                        onClick={() => handleRevokeExternal(row.id, row.recipient_email)}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </RevokeButton>
                    </ShareRow>
                  ))}
                </ShareList>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default ShareDashboardModal;
